/**
 * Formula nutritional values imported from formula-nfp-values.csv
 * This data should be used in production instead of referencing the CSV directly.
 */

export interface NutrientValue {
  value: string
  unit: string
  category: string
}

export interface FormulaData {
  name: string
  nutrients: Record<string, NutrientValue>
}

export const FORMULA_DATA: Record<string, FormulaData> = {
  'Kendamil Organic Infant Formula Powder': {
    name: 'Kendamil Organic Infant Formula Powder',
    nutrients: {
      'Calories per Serving': {
        value: '100',
        unit: '',
        category: ''
      },
      'Prepared Size': {
        value: '5',
        unit: 'oz',
        category: ''
      },
      'Protein': {
        value: '2.2',
        unit: 'g',
        category: 'Macronutrients'
      },
      'Fat': {
        value: '5.3',
        unit: 'g',
        category: 'Macronutrients'
      },
      'Carb': {
        value: '10.5',
        unit: 'g',
        category: 'Macronutrients'
      },
      'Linoleic Acid': {
        value: '880',
        unit: 'mg',
        category: 'Macronutrients'
      },
      'Water': {
        value: 'Not listed',
        unit: 'g',
        category: 'Macronutrients'
      },
      'Vitamin A': {
        value: '250',
        unit: 'IU',
        category: 'Vitamins'
      },
      'Vitamin D': {
        value: '80',
        unit: 'IU',
        category: 'Vitamins'
      },
      'Vitamin E': {
        value: '2.3',
        unit: 'IU',
        category: 'Vitamins'
      },
      'Vitamin K': {
        value: '5',
        unit: 'mcg',
        category: 'Vitamins'
      },
      'Thiamine (Vitamin B1)': {
        value: '100',
        unit: 'mcg',
        category: 'Vitamins'
      },
      'Riboflavin (Vitamin B2)': {
        value: '270',
        unit: 'mcg',
        category: 'Vitamins'
      },
      'Vitamin B6': {
        value: '80',
        unit: 'mcg',
        category: 'Vitamins'
      },
      'Vitamin B12': {
        value: '0.2',
        unit: 'mcg',
        category: 'Vitamins'
      },
      'Niacin': {
        value: '920',
        unit: 'mcg',
        category: 'Vitamins'
      },
      'Folic Acid': {
        value: '17',
        unit: 'mcg',
        category: 'Vitamins'
      },
      'Pantothenic Acid': {
        value: '740',
        unit: 'mcg',
        category: 'Vitamins'
      },
      'Biotin': {
        value: '2.7',
        unit: 'mcg',
        category: 'Vitamins'
      },
      'Vitamin C (Ascorbic Acid)': {
        value: '13',
        unit: 'mg',
        category: 'Vitamins'
      },
      'Choline': {
        value: '30',
        unit: 'mg',
        category: 'Vitamins'
      },
      'Inositol': {
        value: '17',
        unit: 'mg',
        category: 'Vitamins'
      },
      'Calcium': {
        value: '68',
        unit: 'mg',
        category: 'Minerals'
      },
      'Phosphorus': {
        value: '36',
        unit: 'mg',
        category: 'Minerals'
      },
      'Magnesium': {
        value: '9',
        unit: 'mg',
        category: 'Minerals'
      },
      'Iron': {
        value: '1.03',
        unit: 'mg',
        category: 'Minerals'
      },
      'Zinc': {
        value: '0.75',
        unit: 'mg',
        category: 'Minerals'
      },
      'Manganese': {
        value: '13',
        unit: 'mcg',
        category: 'Minerals'
      },
      'Copper': {
        value: '78',
        unit: 'mcg',
        category: 'Minerals'
      },
      'Iodine': {
        value: '18',
        unit: 'mcg',
        category: 'Minerals'
      },
      'Selenium': {
        value: '3.5',
        unit: 'mcg',
        category: 'Minerals'
      },
      'Sodium': {
        value: '37',
        unit: 'mg',
        category: 'Minerals'
      },
      'Potassium': {
        value: '100',
        unit: 'mg',
        category: 'Minerals'
      },
      'Chloride': {
        value: '75',
        unit: 'mg',
        category: 'Minerals'
      },
    }
  },
  'Similac 360 Total Care Infant Formula Powder': {
    name: 'Similac 360 Total Care Infant Formula Powder',
    nutrients: {
      'Calories per Serving': {
        value: '100',
        unit: '',
        category: ''
      },
      'Prepared Size': {
        value: '5',
        unit: 'oz',
        category: ''
      },
      'Protein': {
        value: '2.07',
        unit: 'g',
        category: 'Macronutrients'
      },
      'Fat': {
        value: '5.6',
        unit: 'g',
        category: 'Macronutrients'
      },
      'Carb': {
        value: '10.5',
        unit: 'g',
        category: 'Macronutrients'
      },
      'Linoleic Acid': {
        value: '1000',
        unit: 'mg',
        category: 'Macronutrients'
      },
      'Water': {
        value: '134',
        unit: 'g',
        category: 'Macronutrients'
      },
      'Vitamin A': {
        value: '300',
        unit: 'IU',
        category: 'Vitamins'
      },
      'Vitamin D': {
        value: '60',
        unit: 'IU',
        category: 'Vitamins'
      },
      'Vitamin E': {
        value: '1.5',
        unit: 'IU',
        category: 'Vitamins'
      },
      'Vitamin K': {
        value: '8',
        unit: 'mcg',
        category: 'Vitamins'
      },
      'Thiamine (Vitamin B1)': {
        value: '100',
        unit: 'mcg',
        category: 'Vitamins'
      },
      'Riboflavin (Vitamin B2)': {
        value: '150',
        unit: 'mcg',
        category: 'Vitamins'
      },
      'Vitamin B6': {
        value: '60',
        unit: 'mcg',
        category: 'Vitamins'
      },
      'Vitamin B12': {
        value: '0.25',
        unit: 'mcg',
        category: 'Vitamins'
      },
      'Niacin': {
        value: '1050',
        unit: 'mcg',
        category: 'Vitamins'
      },
      'Folic Acid': {
        value: '15',
        unit: 'mcg',
        category: 'Vitamins'
      },
      'Pantothenic Acid': {
        value: '450',
        unit: 'mcg',
        category: 'Vitamins'
      },
      'Biotin': {
        value: '4.4',
        unit: 'mcg',
        category: 'Vitamins'
      },
      'Vitamin C (Ascorbic Acid)': {
        value: '9',
        unit: 'mg',
        category: 'Vitamins'
      },
      'Choline': {
        value: '24',
        unit: 'mg',
        category: 'Vitamins'
      },
      'Inositol': {
        value: '24',
        unit: 'mg',
        category: 'Vitamins'
      },
      'Calcium': {
        value: '78',
        unit: 'mg',
        category: 'Minerals'
      },
      'Phosphorus': {
        value: '42',
        unit: 'mg',
        category: 'Minerals'
      },
      'Magnesium': {
        value: '6',
        unit: 'mg',
        category: 'Minerals'
      },
      'Iron': {
        value: '1.8',
        unit: 'mg',
        category: 'Minerals'
      },
      'Zinc': {
        value: '0.75',
        unit: 'mg',
        category: 'Minerals'
      },
      'Manganese': {
        value: '5',
        unit: 'mcg',
        category: 'Minerals'
      },
      'Copper': {
        value: '90',
        unit: 'mcg',
        category: 'Minerals'
      },
      'Iodine': {
        value: '15',
        unit: 'mcg',
        category: 'Minerals'
      },
      'Selenium': {
        value: '2',
        unit: 'mcg',
        category: 'Minerals'
      },
      'Sodium': {
        value: '24',
        unit: 'mg',
        category: 'Minerals'
      },
      'Potassium': {
        value: '105',
        unit: 'mg',
        category: 'Minerals'
      },
      'Chloride': {
        value: '65',
        unit: 'mg',
        category: 'Minerals'
      },
    }
  },
  'Bobbie Organic Infant Formula': {
    name: 'Bobbie Organic Infant Formula',
    nutrients: {
      'Calories per Serving': {
        value: '100',
        unit: '',
        category: ''
      },
      'Prepared Size': {
        value: '5',
        unit: 'oz',
        category: ''
      },
      'Protein': {
        value: '2.0',
        unit: 'g',
        category: 'Macronutrients'
      },
      'Fat': {
        value: '5.3',
        unit: 'g',
        category: 'Macronutrients'
      },
      'Carb': {
        value: '11',
        unit: 'g',
        category: 'Macronutrients'
      },
      'Linoleic Acid': {
        value: '725',
        unit: 'mg',
        category: 'Macronutrients'
      },
      'Water': {
        value: '136',
        unit: 'g',
        category: 'Macronutrients'
      },
      'Vitamin A': {
        value: '300',
        unit: 'IU',
        category: 'Vitamins'
      },
      'Vitamin D': {
        value: '75',
        unit: 'IU',
        category: 'Vitamins'
      },
      'Vitamin E': {
        value: '2',
        unit: 'IU',
        category: 'Vitamins'
      },
      'Vitamin K': {
        value: '8.3',
        unit: 'mcg',
        category: 'Vitamins'
      },
      'Thiamine (Vitamin B1)': {
        value: '100',
        unit: 'mcg',
        category: 'Vitamins'
      },
      'Riboflavin (Vitamin B2)': {
        value: '150',
        unit: 'mcg',
        category: 'Vitamins'
      },
      'Vitamin B6': {
        value: '63',
        unit: 'mcg',
        category: 'Vitamins'
      },
      'Vitamin B12': {
        value: '0.2',
        unit: 'mcg',
        category: 'Vitamins'
      },
      'Niacin': {
        value: '1000',
        unit: 'mcg',
        category: 'Vitamins'
      },
      'Folic Acid': {
        value: '15',
        unit: 'mcg',
        category: 'Vitamins'
      },
      'Pantothenic Acid': {
        value: '450',
        unit: 'mcg',
        category: 'Vitamins'
      },
      'Biotin': {
        value: '3',
        unit: 'mcg',
        category: 'Vitamins'
      },
      'Vitamin C (Ascorbic Acid)': {
        value: '9',
        unit: 'mg',
        category: 'Vitamins'
      },
      'Choline': {
        value: '25',
        unit: 'mg',
        category: 'Vitamins'
      },
      'Inositol': {
        value: '24',
        unit: 'mg',
        category: 'Vitamins'
      },
      'Calcium': {
        value: '63',
        unit: 'mg',
        category: 'Minerals'
      },
      'Phosphorus': {
        value: '42',
        unit: 'mg',
        category: 'Minerals'
      },
      'Magnesium': {
        value: '7',
        unit: 'mg',
        category: 'Minerals'
      },
      'Iron': {
        value: '1.2',
        unit: 'mg',
        category: 'Minerals'
      },
      'Zinc': {
        value: '0.75',
        unit: 'mg',
        category: 'Minerals'
      },
      'Manganese': {
        value: '5',
        unit: 'mcg',
        category: 'Minerals'
      },
      'Copper': {
        value: '71',
        unit: 'mcg',
        category: 'Minerals'
      },
      'Iodine': {
        value: '15',
        unit: 'mcg',
        category: 'Minerals'
      },
      'Selenium': {
        value: '3',
        unit: 'mcg',
        category: 'Minerals'
      },
      'Sodium': {
        value: '27',
        unit: 'mg',
        category: 'Minerals'
      },
      'Potassium': {
        value: '110',
        unit: 'mg',
        category: 'Minerals'
      },
      'Chloride': {
        value: '65',
        unit: 'mg',
        category: 'Minerals'
      },
    }
  },
  'Holle Stage 1 Formula Organic (Cow)': {
    name: 'Holle Stage 1 Formula Organic (Cow)',
    nutrients: {
      'Calories per Serving': {
        value: '100',
        unit: '',
        category: ''
      },
      'Prepared Size': {
        value: '5',
        unit: 'oz',
        category: ''
      },
      'Protein': {
        value: '2.1',
        unit: 'g',
        category: 'Macronutrients'
      },
      'Fat': {
        value: '5.0',
        unit: 'g',
        category: 'Macronutrients'
      },
      'Carb': {
        value: '11.8',
        unit: 'g',
        category: 'Macronutrients'
      },
      'Linoleic Acid': {
        value: '803',
        unit: 'mg',
        category: 'Macronutrients'
      },
      'Water': {
        value: 'Not listed',
        unit: 'g',
        category: 'Macronutrients'
      },
      'Vitamin A': {
        value: '268.4',
        unit: 'IU',
        category: 'Vitamins'
      },
      'Vitamin D': {
        value: '88.2',
        unit: 'IU',
        category: 'Vitamins'
      },
      'Vitamin E': {
        value: '1.99',
        unit: 'IU',
        category: 'Vitamins'
      },
      'Vitamin K': {
        value: '7.9',
        unit: 'mcg',
        category: 'Vitamins'
      },
      'Thiamine (Vitamin B1)': {
        value: '51.5',
        unit: 'mcg',
        category: 'Vitamins'
      },
      'Riboflavin (Vitamin B2)': {
        value: '264.7',
        unit: 'mcg',
        category: 'Vitamins'
      },
      'Vitamin B6': {
        value: '44.1',
        unit: 'mcg',
        category: 'Vitamins'
      },
      'Vitamin B12': {
        value: '0.21',
        unit: 'mcg',
        category: 'Vitamins'
      },
      'Niacin': {
        value: '514.7',
        unit: 'mcg',
        category: 'Vitamins'
      },
      'Folic Acid': {
        value: '14.9',
        unit: 'mcg',
        category: 'Vitamins'
      },
      'Pantothenic Acid': {
        value: '515',
        unit: 'mcg',
        category: 'Vitamins'
      },
      'Biotin': {
        value: '2.4',
        unit: 'mcg',
        category: 'Vitamins'
      },
      'Vitamin C (Ascorbic Acid)': {
        value: '9.0',
        unit: 'mg',
        category: 'Vitamins'
      },
      'Choline': {
        value: '32',
        unit: 'mg',
        category: 'Vitamins'
      },
      'Inositol': {
        value: '6.8',
        unit: 'mg',
        category: 'Vitamins'
      },
      'Calcium': {
        value: '88.1',
        unit: 'mg',
        category: 'Minerals'
      },
      'Phosphorus': {
        value: '52.8',
        unit: 'mg',
        category: 'Minerals'
      },
      'Magnesium': {
        value: '7.94',
        unit: 'mg',
        category: 'Minerals'
      },
      'Iron': {
        value: '0.794',
        unit: 'mg',
        category: 'Minerals'
      },
      'Zinc': {
        value: '0.66',
        unit: 'mg',
        category: 'Minerals'
      },
      'Manganese': {
        value: '7.8',
        unit: 'mcg',
        category: 'Minerals'
      },
      'Copper': {
        value: '81',
        unit: 'mcg',
        category: 'Minerals'
      },
      'Iodine': {
        value: '20.9',
        unit: 'mcg',
        category: 'Minerals'
      },
      'Selenium': {
        value: '5.7',
        unit: 'mcg',
        category: 'Minerals'
      },
      'Sodium': {
        value: '35',
        unit: 'mg',
        category: 'Minerals'
      },
      'Potassium': {
        value: '121',
        unit: 'mg',
        category: 'Minerals'
      },
      'Chloride': {
        value: '75',
        unit: 'mg',
        category: 'Minerals'
      },
    }
  },
  'Enfamil NeuroPro Powder Infant Formula': {
    name: 'Enfamil NeuroPro Powder Infant Formula',
    nutrients: {
      'Calories per Serving': {
        value: '100',
        unit: '',
        category: ''
      },
      'Prepared Size': {
        value: '5',
        unit: 'oz',
        category: ''
      },
      'Protein': {
        value: '2.1',
        unit: 'g',
        category: 'Macronutrients'
      },
      'Fat': {
        value: '5.3',
        unit: 'g',
        category: 'Macronutrients'
      },
      'Carb': {
        value: '11.3',
        unit: 'g',
        category: 'Macronutrients'
      },
      'Linoleic Acid': {
        value: '780',
        unit: 'mg',
        category: 'Macronutrients'
      },
      'Water': {
        value: '133',
        unit: 'g',
        category: 'Macronutrients'
      },
      'Vitamin A': {
        value: '300',
        unit: 'IU',
        category: 'Vitamins'
      },
      'Vitamin D': {
        value: '75',
        unit: 'IU',
        category: 'Vitamins'
      },
      'Vitamin E': {
        value: '2',
        unit: 'IU',
        category: 'Vitamins'
      },
      'Vitamin K': {
        value: '9',
        unit: 'mcg',
        category: 'Vitamins'
      },
      'Thiamine (Vitamin B1)': {
        value: '80',
        unit: 'mcg',
        category: 'Vitamins'
      },
      'Riboflavin (Vitamin B2)': {
        value: '140',
        unit: 'mcg',
        category: 'Vitamins'
      },
      'Vitamin B6': {
        value: '60',
        unit: 'mcg',
        category: 'Vitamins'
      },
      'Vitamin B12': {
        value: '0.3',
        unit: 'mcg',
        category: 'Vitamins'
      },
      'Niacin': {
        value: '1000',
        unit: 'mcg',
        category: 'Vitamins'
      },
      'Folic Acid': {
        value: '16',
        unit: 'mcg',
        category: 'Vitamins'
      },
      'Pantothenic Acid': {
        value: '500',
        unit: 'mcg',
        category: 'Vitamins'
      },
      'Biotin': {
        value: '3',
        unit: 'mcg',
        category: 'Vitamins'
      },
      'Vitamin C (Ascorbic Acid)': {
        value: '12',
        unit: 'mg',
        category: 'Vitamins'
      },
      'Choline': {
        value: '24',
        unit: 'mg',
        category: 'Vitamins'
      },
      'Inositol': {
        value: '24',
        unit: 'mg',
        category: 'Vitamins'
      },
      'Calcium': {
        value: '78',
        unit: 'mg',
        category: 'Minerals'
      },
      'Phosphorus': {
        value: '43',
        unit: 'mg',
        category: 'Minerals'
      },
      'Magnesium': {
        value: '8',
        unit: 'mg',
        category: 'Minerals'
      },
      'Iron': {
        value: '1.8',
        unit: 'mg',
        category: 'Minerals'
      },
      'Zinc': {
        value: '1',
        unit: 'mg',
        category: 'Minerals'
      },
      'Manganese': {
        value: '15',
        unit: 'mcg',
        category: 'Minerals'
      },
      'Copper': {
        value: '75',
        unit: 'mcg',
        category: 'Minerals'
      },
      'Iodine': {
        value: '15',
        unit: 'mcg',
        category: 'Minerals'
      },
      'Selenium': {
        value: '2.8',
        unit: 'mcg',
        category: 'Minerals'
      },
      'Sodium': {
        value: '27',
        unit: 'mg',
        category: 'Minerals'
      },
      'Potassium': {
        value: '108',
        unit: 'mg',
        category: 'Minerals'
      },
      'Chloride': {
        value: '63',
        unit: 'mg',
        category: 'Minerals'
      },
    }
  },
}

/**
 * Get nutrient value for a specific formula
 */
export function getFormulaNutrient(formulaName: string, nutrient: string): NutrientValue | undefined {
  return FORMULA_DATA[formulaName]?.nutrients[nutrient]
}

/**
 * Get all formulas
 */
export function getAllFormulas(): string[] {
  return Object.keys(FORMULA_DATA)
}

/**
 * Get all nutrients for a formula
 */
export function getFormulaNutrients(formulaName: string): Record<string, NutrientValue> | undefined {
  return FORMULA_DATA[formulaName]?.nutrients
}
